// Evidence (DESIGN §3, DATA_CONTRACT G): documents in the vault, two-reader values under the blind rule, disputes, staged
// values and the engine's reading tasks. The published view decides what you see: a first reading stays hidden from everyone
// but its reader until a second reader has read it blind; a disputed reading shows to the readers and managers. The second
// reading is typed by the person (never prefilled); a reader cannot verify their own reading; settling a dispute is a
// manager's own act. Where firm policy makes the second reading optional (ADR-0027) a person may instead accept a first reading
// as read (evidence.accept, their own act); the value then shows "accepted by X — single reader", never as a blind verification.
import { useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { AgentChip, Card, DataTable, StatusPill } from "../../components";
import type { EvidenceValue } from "../../data/types";
import { fmtStamp, plural, titleCase } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { Act, NotPublished, QueueList, useAct } from "./shared";

export function EvidencePage() {
  const { id, model } = useEngagement();
  const { ds, me } = useApp();
  const { open, drawer } = useAct();
  const [params] = useSearchParams();
  const docs = useLoad(() => ds.table(id, "documents"), [ds, id]);
  const values = useLoad(() => ds.table(id, "evidence_values_visible"), [ds, id]);
  const docName = new Map((docs.data ?? []).map((d) => [d.sha256, d.originalName]));
  const disputes = (values.data ?? []).filter((v) => v.status === "disputed");
  const tasks = model.actions.filter((a) => a.command.startsWith("evidence.") || a.command === "staged.apply");
  const mineRead = (v: EvidenceValue) => v.readBy === me.personName;
  const accept = (v: EvidenceValue) => <Act command="evidence.accept" title={`Accept value ${v.id} as read (single reader): ${v.label}`} open={open}
    fixed={{ ids: [Number(v.id)] }} label="Accept…" />;

  const act = (v: EvidenceValue) => {
    if (v.status === "read") {
      return mineRead(v) ? <span className="flex flex-wrap items-center justify-end gap-1.5"><span className="text-xs text-muted">Your reading — a second reader verifies it, or a person accepts it where firm policy allows.</span>{accept(v)}</span>
        : <span className="flex flex-wrap items-center justify-end gap-1.5"><Act command="evidence.verify" title={`Second reading of value ${v.id}: ${v.label} (blind)`} open={open} fixed={{ id: Number(v.id) }} label="Second reading…" />{accept(v)}</span>;
    }
    if (v.status === "disputed") return <Act command="evidence.settle" title={`Settle the dispute on value ${v.id}: ${v.label}`} open={open} fixed={{ id: Number(v.id) }} label="Settle…" />;
    return null;
  };

  // ?doc=<sha256> (from an issue's sources): the document and every value read from it, first.
  const focus = params.get("doc");
  const fdoc = focus ? (docs.data ?? []).find((d) => d.sha256 === focus || d.sha256.startsWith(focus)) : undefined;
  const fvals = fdoc ? (values.data ?? []).filter((v) => v.document === fdoc.sha256) : [];

  return (
    <div className="space-y-4">
      {focus && (
        <Card title={fdoc ? `Document: ${fdoc.originalName}` : "Document"} className="border-brand/40">
          {!fdoc ? <p className="text-sm text-muted">{docs.loading ? "Loading…" : `No document ${focus.slice(0, 16)} in this engagement's vault that you can see.`}</p> : (
            <div className="space-y-2 text-sm">
              <p className="text-muted">Client folder {fdoc.pbcFolder ?? "—"} · received {fmtStamp(fdoc.receivedAt)}{fdoc.receivedBy ? ` by ${fdoc.receivedBy}` : ""} · SHA-256 <span className="font-mono text-xs">{fdoc.sha256.slice(0, 16)}</span></p>
              {fvals.length === 0 ? <p className="text-muted">No values have been read from it.</p> : (
                <ul className="divide-y divide-line" aria-label="Values read from this document">
                  {fvals.map((v) => (
                    <li key={v.id} className="flex flex-wrap items-baseline gap-2 py-1.5">
                      <span className="min-w-0 flex-1">{v.label}<span className="block text-xs text-muted">{v.page ? `page ${v.page} · ` : ""}{v.locator}</span></span>
                      <span className="font-mono">{v.valueHidden ? <span className="text-xs italic text-muted">hidden: a blind reading</span> : v.value ?? "—"}</span>
                      <StatusPill state={v.status} />
                      <span className="w-full text-xs text-muted">{v.readBy ? `first reading: ${v.readBy}` : ""}{confirmedText(v) ? ` · ${confirmedText(v)}` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}
      {disputes.length > 0 && (
        <Card title={`Disputes · ${disputes.length}`} className="border-blocked-line">
          <p className="mb-2 text-sm text-muted">Two readers disagreed. A manager settles each with the value and the reason (their own act); the readings stay in the record.</p>
          <ul className="divide-y divide-line">
            {disputes.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="min-w-0 flex-1">{v.label} <span className="text-xs text-muted">· {docName.get(v.document) ?? v.document.slice(0, 12)}{v.page ? ` p.${v.page}` : ""}</span>
                  <span className="block text-xs text-muted">read by {v.readBy ?? "—"}{v.readAgent ? ` (${v.readAgent})` : ""} · second reading by {v.verifiedBy ?? "—"}</span></span>
                <span className="font-mono">{v.valueHidden ? <span className="text-xs italic text-muted">hidden from you</span> : v.value}</span>
                {act(v)}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Values read from documents" bodyClass="p-3">
        <DataTable rows={values.data} loading={values.loading} error={values.error} onRetry={values.reload} rowKey={(v) => v.id} caption="Evidence values" dense
          filter={{ id: "status", label: "status", value: (v) => v.status, labelFor: titleCase }}
          empty="No values read yet. Readings appear here as agents and people read the client's documents."
          columns={[
            { id: "label", header: "Value", cell: (v) => <span>{v.label}<span className="block font-mono text-[11px] text-muted">{docName.get(v.document) ?? v.document.slice(0, 16)}{v.locator ? ` · ${v.locator}` : ""}</span></span> },
            { id: "page", header: "Page", align: "right", cell: (v) => v.page ?? "—" },
            { id: "value", header: "Reading", align: "right", cell: (v) => (v.valueHidden ? <span className="text-xs italic text-muted">hidden: a blind reading waits for its second reader</span> : v.value === null ? "—" : <span className="font-mono">{v.value}</span>) },
            { id: "status", header: "State", cell: (v) => <StatusPill state={v.status} /> },
            { id: "who", header: "Readers", cell: (v) => <span className="flex flex-wrap items-center gap-1 text-xs">{v.readAgent && <AgentChip name={v.readAgent} />}{v.readBy ? `first reading: ${v.readBy}` : ""}{confirmedText(v) ? ` · ${confirmedText(v)}` : ""}{v.method ? ` · ${v.method}` : ""}</span> },
            { id: "act", header: <span className="sr-only">Act</span>, align: "right", cell: act },
          ]} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Reading tasks from the engine" className="min-w-0" bodyClass="p-3">
          <QueueList actions={tasks} open={open} empty="No reading tasks in the engine's queue." />
        </Card>
        <Card title="Staged values" className="min-w-0">
          <p className="mb-2 text-sm text-muted">Statement and cash readings the engine staged from documents become records only when applied.</p>
          <NotPublished what="the list of staged values" command="statement.list" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Act command="staged.apply" title="Apply staged statement values" open={open} fixed={{ kind: "statement" }} label="Apply statements…" />
            <Act command="staged.apply" title="Apply staged cash values" open={open} fixed={{ kind: "cash" }} label="Apply cash…" />
          </div>
        </Card>
      </div>

      <Card title={`Documents${docs.data ? ` · ${docs.data.length}` : ""}`} bodyClass="p-3">
        <DataTable rows={docs.data} loading={docs.loading} error={docs.error} onRetry={docs.reload} rowKey={(d) => d.sha256} caption="Documents" dense
          filter={{ id: "folder", label: "client folder", value: (d) => d.pbcFolder ?? "none" }}
          empty="No documents in the vault yet. Client uploads land here after the engine receives them."
          columns={[
            { id: "name", header: "Document", cell: (d) => <span>{d.originalName}{d.supersededAt && <span className="block text-xs text-muted">superseded {fmtStamp(d.supersededAt)}</span>}</span>, sortValue: (d) => d.originalName },
            { id: "folder", header: "Client folder", cell: (d) => d.pbcFolder ?? "—", sortValue: (d) => d.pbcFolder ?? "" },
            { id: "sha", header: "SHA-256", cell: (d) => <span className="font-mono text-xs" title={d.sha256}>{d.sha256.slice(0, 16)}</span> },
            { id: "recv", header: "Received", cell: (d) => <span className="text-xs">{d.receivedBy ?? "—"} · {fmtStamp(d.receivedAt)}{d.requestId ? ` · request ${d.requestId}` : ""}</span>, sortValue: (d) => d.receivedAt },
            { id: "read", header: <span className="sr-only">Act</span>, align: "right", cell: (d) => <Act command="evidence.read" title={`Read a value from ${d.originalName}`} open={open} fixed={{ doc: d.sha256 }} label="Read a value…" /> },
          ]} />
        <p className="mt-2 text-xs text-muted">{plural((values.data ?? []).filter((v) => v.valueHidden).length, "reading")} hidden from you by the blind rule. Adding a document to the vault runs on the engine's machine (<span className="font-mono">yvc vault add</span>).</p>
      </Card>
      {drawer}
    </div>
  );
}

/** How a usable value was confirmed (ADR-0027): an accept is a single reader, shown apart from a blind second reading. */
export function confirmedText(v: EvidenceValue): string {
  const who = (p?: string | null, a?: string | null) => `${p ?? "—"}${a ? ` (via ${a})` : ""}`;
  if (v.status === "verified" && v.confirmedHow === "accepted") return `accepted by ${who(v.acceptedBy, v.acceptedAgent)} — single reader`;
  if (v.status === "verified" && v.confirmedHow === "settled") return `settled from the document by ${who(v.verifiedBy, v.verifiedAgent)}`;
  if (v.verifiedBy) return `${v.status === "verified" ? "verified blind by" : "second reading by"} ${who(v.verifiedBy, v.verifiedAgent)}`;
  return "";
}
